import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const categoryColors: Record<string, string> = {
  '維修案例': 'bg-blue-100 text-blue-600',
  '優惠活動': 'bg-orange-100 text-orange-600',
  '公司動態': 'bg-green-100 text-green-600',
  '維修教學': 'bg-purple-100 text-purple-600',
};

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  category: string;
  image: string;
  published_at: string;
  published: boolean;
}

export default function NewsSection() {
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const { data, error } = await supabase
        .from('news_articles')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      setNews(data || []);
    } catch (err) {
      console.error('Failed to fetch news:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="inline-flex items-center gap-2 text-orange-500 text-sm font-medium mb-3">
              <span className="w-8 h-px bg-orange-500" />
              最新消息
            </div>
            <h2 className="text-4xl font-bold text-gray-900">消息與維修案例</h2>
          </div>
          <button
            onClick={() => navigate('/news')}
            className="hidden md:flex items-center gap-1.5 text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line" />
            </div>
          </button>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {news.map((article) => (
                <div
                  key={article.id}
                  onClick={() => navigate(`/news/${article.id}`)}
                  className="bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer border border-gray-100"
                >
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 right-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${categoryColors[article.category] ?? 'bg-gray-100 text-gray-600'}`}>
                        {article.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-2 text-gray-400 text-xs mb-3">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-calendar-line" />
                      </div>
                      {article.published_at}
                    </div>

                    <h3 className="font-bold text-gray-900 text-base leading-snug mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4">
                      {article.summary}
                    </p>

                    <div className="flex items-center gap-1 text-orange-500 text-sm font-medium">
                      閱讀更多
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-sm" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10 md:hidden">
              <button
                onClick={() => navigate('/news')}
                className="px-8 py-3 border-2 border-orange-500 text-orange-500 font-semibold rounded-full cursor-pointer"
              >
                查看全部消息
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}